import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { fmt, downloadFile } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "./Dashboard";
import GeoMap from "@/components/GeoMap";
import Creative from "@/components/Creative";
import { TrendChart, MetricBarChart } from "@/components/Charts";

export default function CampaignDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    api.get(`/client/campaigns/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load campaign"));
  }, [id]);

  if (!data) return <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 bg-white/5" />)}</div>;

  const { campaign, geofences, analytics } = data;
  const t = analytics.totals;

  const download = async (format) => {
    setDownloading(format);
    try {
      await downloadFile(`/client/reports/campaign/${id}?format=${format}`, `${campaign.name.replace(/\s+/g, "_")}_report.${format}`);
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch {
      toast.error("Report generation failed");
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="space-y-6" data-testid="campaign-detail">
      <Link to="/portal/campaigns" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors">
        <ArrowLeft className="w-4 h-4" /> All campaigns
      </Link>

      <div className="card-surface p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="campaign-name">{campaign.name}</h2>
              <StatusBadge status={campaign.status} />
            </div>
            <p className="text-sm text-slate-400 mt-2">
              Manager: {campaign.manager} · {campaign.start_date} → {campaign.end_date} · <span className="text-cyan-300 font-semibold" data-testid="campaign-duration">{campaign.duration_days} days</span>
            </p>
            {campaign.objective && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{campaign.objective}</p>}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="border-white/10" disabled={!!downloading} onClick={() => download("csv")} data-testid="download-csv">
              <Download className="w-4 h-4 mr-2" /> {downloading === "csv" ? "Generating..." : "CSV"}
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-500" disabled={!!downloading} onClick={() => download("pdf")} data-testid="download-pdf">
              <Download className="w-4 h-4 mr-2" /> {downloading === "pdf" ? "Generating..." : "GENERATE REPORT"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
        <div className="space-y-6">
          <div className="card-surface p-5">
            <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">CAMPAIGN MAP</h3>
            <GeoMap geofences={geofences.map((g) => ({ ...g, campaign_name: campaign.name, metrics: analytics.locations.find((l) => l.geofence_id === g.id) }))} height="440px" />
            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              {geofences.map((g) => (
                <div key={g.id} className="rounded-md border border-white/5 bg-secondary/30 p-3" data-testid={`geofence-chip-${g.id}`}>
                  <p className="text-xs font-semibold text-white flex items-center gap-1.5"><MapPin className="w-3 h-3 text-cyan-400" /> {g.location_name}</p>
                  <p className="text-[10px] text-slate-500 mt-1">{g.radius_meters}m · {g.start_date} → {g.end_date}</p>
                  <p className="text-[10px] text-slate-500 font-mono">{g.latitude.toFixed(4)}, {g.longitude.toFixed(4)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card-surface p-5">
            <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">IMPRESSIONS &amp; ENGAGEMENTS OVER TIME</h3>
            <TrendChart data={analytics.series} keys={[
              { key: "impressions", color: "#3b82f6", name: "Impressions" },
              { key: "engagements", color: "#06b6d4", name: "Engagements" },
            ]} height={300} />
          </div>

          <div className="card-surface p-5">
            <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">LOCATION PERFORMANCE</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="location-performance-table">
                <thead>
                  <tr className="text-left text-[10px] tracking-widest text-slate-500 uppercase border-b border-white/5">
                    <th className="pb-3 pr-4">Location</th><th className="pb-3 pr-4 text-right">Impressions</th>
                    <th className="pb-3 pr-4 text-right">Reach</th><th className="pb-3 pr-4 text-right">Engagements</th>
                    <th className="pb-3 text-right">Eng. Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {analytics.locations.map((l) => (
                    <tr key={l.geofence_id} className="text-slate-300">
                      <td className="py-3 pr-4 font-semibold text-white">{l.location_name}</td>
                      <td className="py-3 pr-4 text-right">{fmt(l.impressions)}</td>
                      <td className="py-3 pr-4 text-right">{fmt(l.reach)}</td>
                      <td className="py-3 pr-4 text-right">{fmt(l.engagements)}</td>
                      <td className="py-3 text-right text-cyan-300 font-semibold">{l.engagement_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card-surface p-5">
            <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">CREATIVE PREVIEW</h3>
            <Creative url={campaign.creative_url} type={campaign.creative_type} className="w-full aspect-video" />
            <p className="text-xs text-slate-500 mt-3">{campaign.creative_type} creative · {campaign.currency} {fmt(campaign.budget)} budget</p>
          </div>

          <div className="card-surface p-5">
            <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">KEY METRICS</h3>
            <div className="grid grid-cols-2 gap-3">
              {[["Impressions", t.impressions], ["Reach", t.reach], ["Engagements", t.engagements], ["Clicks", t.clicks], ["Eng. Rate", `${t.engagement_rate}%`], ["CTR", `${t.ctr}%`]].map(([label, v]) => (
                <div key={label} className="rounded-md border border-white/5 bg-secondary/30 p-3">
                  <p className="font-display font-bold text-white">{typeof v === "number" ? fmt(v) : v}</p>
                  <p className="text-[10px] tracking-widest text-slate-500 uppercase mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card-surface p-5" data-testid="campaign-cost-chart">
            <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">CAMPAIGN COST PER LOCATION</h3>
            <MetricBarChart
              data={analytics.locations.map((l) => ({ name: l.location_name.replace("Johannesburg", "JHB"), cost: l.cost }))}
              barKey="cost" color="#0ea5e9" height={220} />
            <div className="grid grid-cols-3 gap-2 mt-3">
              {analytics.locations.map((l) => (
                <div key={l.geofence_id} className="text-center rounded-md border border-white/5 bg-secondary/30 p-2">
                  <p className="text-sm font-bold text-cyan-300">R{(l.cost || 0).toLocaleString("en-ZA")}</p>
                  <p className="text-[10px] text-slate-500 truncate">{l.location_name}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3 text-center">
              Total media investment <span className="text-white font-semibold">R{(campaign.budget || 0).toLocaleString("en-ZA")}</span> · R45,000 per transit site · R20,000 one-day DHL Stadium activation
            </p>
          </div>

          <div className="card-surface p-5">
            <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">REACH BY LOCATION</h3>
            <MetricBarChart data={analytics.locations.map((l) => ({ name: l.location_name.split(" ")[0], reach: l.reach }))} barKey="reach" />
          </div>
        </div>
      </div>
    </div>
  );
}
